export interface R8Entity {
  tevunaId?: string;
  name?: string;
  email?: string;
  phone?: string;
  registeredAddress?: string;
  country?: string;
  city?: string;
  confidenceScore?: string;
}

export interface R8OwnershipLevel {
  tevunaId?: string;
  name?: string;
  address?: string;
  city?: string;
  country?: string;
  email?: string;
  website?: string;
}

export interface R8Ubo {
  tevunaId?: string;
  name?: string;
  country?: string;
  startDate?: string;
  percentage?: string;
  confidence?: string;
  reasoning?: string;
  entityType?: string;
}

export interface R8ReportModel {
  vesselImo: string;
  shipOwner: R8Entity | null;
  shipManager: R8Entity | null;
  ownershipLevels: R8OwnershipLevel[];
  ubos: R8Ubo[];
  references: string[];
}

type Data = any;

function asData(value: unknown): Data {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Data : {};
}

function parseData(value: unknown): Data {
  if (typeof value !== 'string') return asData(value);
  try { return asData(JSON.parse(value)); } catch { return {}; }
}

function clean(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  return !text || ['-', '99', 'n/a', 'null', 'undefined'].includes(text.toLowerCase()) ? undefined : text;
}

function first(...values: unknown[]): string | undefined {
  for (const value of values) {
    const result = clean(value);
    if (result !== undefined) return result;
  }
  return undefined;
}

function populated<T extends Record<string, unknown>>(values: T): Partial<T> | null {
  const output: Record<string, unknown> = {};
  Object.entries(values).forEach(([key, value]) => {
    const normalized = clean(value);
    if (normalized !== undefined) output[key] = normalized;
  });
  return Object.keys(output).length ? output as Partial<T> : null;
}

function partyData(party: Data): Data {
  return { ...party, ...asData(party.vesselData), ...asData(party.tevunaData) };
}

function normalizeParty(rawParty: unknown, confidence: unknown): R8Entity | null {
  const party = asData(rawParty);
  const detail = partyData(party);
  return populated({
    tevunaId: first(party.tevunaId, party.tevuna_id, detail.tevunaId, detail.tevuna_id),
    name: first(party.companyName, party.name, detail.companyName, detail.name),
    email: first(party.email, detail.email),
    phone: first(party.phone, detail.phone),
    registeredAddress: first(party.registeredAddress, party.address, detail.registeredAddress, detail.address),
    country: first(party.country, detail.country),
    city: first(party.city, detail.city),
    confidenceScore: first(party.confidenceScore, party.confidenceLevel, confidence, detail.confidenceScore, detail.confidenceLevel)
  }) as R8Entity | null;
}

function normalizeOwnershipLevels(vesselData: Data, owner: Data): R8OwnershipLevel[] {
  const ownerData = partyData(owner);
  const group = asData(ownerData.groupStructure ?? asData(ownerData.tevunaData).groupStructure);
  const source = Array.isArray(vesselData.ownershipLevels)
    ? vesselData.ownershipLevels
    : [group.ownership_1, group.ownership_2, group.ownership_3];
  return source.map((value: unknown) => {
    const level = asData(value);
    return populated({
      tevunaId: first(level.tevunaId, level.tevuna_id, level.uuid),
      name: first(level.companyName, level.name),
      address: first(level.registeredAddress, level.address),
      city: level.city,
      country: level.country,
      email: level.email,
      website: level.website
    });
  }).filter((level: Partial<R8OwnershipLevel> | null): level is R8OwnershipLevel => !!level);
}

function normalizeUbos(vesselData: Data): R8Ubo[] {
  const source = vesselData.shipOwnerUbo;
  const entries = Array.isArray(source) ? source : source ? [source] : [];
  return entries.map((value: unknown) => {
    const ubo = asData(value);
    return populated({
      tevunaId: first(ubo.tevunaId, ubo.tevuna_id, ubo.uuid),
      name: ubo.name,
      country: ubo.country,
      startDate: first(ubo.startDate, ubo.start_date),
      percentage: first(ubo.percentage, ubo.ownershipPercentage, ubo.ownership_percentage),
      confidence: first(ubo.confidence, ubo.confidenceLevel, ubo.confidence_level),
      reasoning: first(ubo.reasoning, ubo.reason),
      entityType: first(ubo.entityType, ubo.entity_type, ubo.type)
    });
  }).filter((ubo: Partial<R8Ubo> | null): ubo is R8Ubo => !!ubo);
}

function collectUrls(source: unknown, output: Set<string>): void {
  if (typeof source === 'string') {
    const urls = source.match(/https?:\/\/[^\s,\]"')]+/gi) ?? [];
    urls.forEach(url => output.add(url.replace(/[.)]+$/, '')));
  } else if (Array.isArray(source)) {
    source.forEach(value => collectUrls(value, output));
  } else if (source && typeof source === 'object') {
    Object.values(source as Data).forEach(value => collectUrls(value, output));
  }
}

export function normalizeR8Report(source: unknown): R8ReportModel {
  const root = asData(source);
  const details = parseData(root.repDetails ?? root.rep_details ?? root.rep_details_json);
  const vesselData = asData(root.vesselData ?? details.vesselData ?? root.vessel_data);
  const owner = asData(vesselData.shipOwner ?? vesselData.shipowner);
  const manager = asData(vesselData.shipManager ?? vesselData.shipmanager);
  const referenceSet = new Set<string>();
  [
    vesselData.references,
    vesselData.source_urls_used,
    vesselData.sourceUrlsUsed,
    asData(vesselData.risk).urls,
    asData(vesselData.risk).references,
    partyData(owner).references,
    partyData(owner).groupStructureReferences,
    partyData(manager).references,
    partyData(manager).groupStructureReferences
  ].forEach(value => collectUrls(value, referenceSet));

  return {
    vesselImo: first(vesselData.imoNumber, vesselData.imo, asData(vesselData.ship).imoNumber, asData(vesselData.ship).imo, root.imoNumber, root.imo) ?? '',
    shipOwner: normalizeParty(owner, vesselData.shipowner_confidenceLevel ?? vesselData.shipOwnerConfidenceLevel),
    shipManager: normalizeParty(manager, vesselData.shipmanager_confidenceLevel ?? vesselData.shipManagerConfidenceLevel),
    ownershipLevels: normalizeOwnershipLevels(vesselData, owner),
    ubos: normalizeUbos(vesselData),
    references: [...referenceSet]
  };
}
